import type {
  Me,
  MonthOnly,
  OvertimeRequestsResponse,
  OvertimeResponse,
  RequestStatus,
} from "@shared/api"
import { getSupabase } from "../config/supabaseClient"

/**
 * Cliente tipado de /api/dashboard/*.
 *
 * Todos los tipos vienen de @shared/api, que es el MISMO fuente que compila el
 * servidor (src/shared/api.ts). No hay codegen ni duplicación: si mañana la
 * respuesta gana un campo, las dos puntas se enteran en el mismo commit.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * Refresh SINGLE-FLIGHT compartido entre callers concurrentes: varios requests en
 * paralelo que reciben 401 disparan UN refresh, no varios (refreshes en paralelo se
 * invalidan entre sí y terminan cerrando la sesión).
 */
let refreshing: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = getSupabase()
      .auth.refreshSession()
      .then(({ data, error }) => !error && !!data.session)
      .catch(() => false)
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

/** Se pone en true cuando un 401 sobrevive al refresh, para que SignIn explique por qué. */
export let sessionExpired = false

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  // getSession() devuelve la sesión persistida y la refresca sola si expiró. NUNCA
  // leer el token de localStorage a mano ni cachearlo en una variable de módulo: se
  // quedaría viejo justo después de un refresh automático.
  const { data } = await getSupabase().auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new ApiError(401, "no_session", "No hay sesión activa.")

  const headers = new Headers(init.headers)
  headers.set("Authorization", `Bearer ${token}`)
  headers.set("Accept", "application/json")

  const res = await fetch(`/api/dashboard${path}`, { ...init, headers })

  if (res.status === 401 && !retried) {
    if (await refreshOnce()) return request<T>(path, init, true)
    sessionExpired = true
    await getSupabase().auth.signOut()
    throw new ApiError(401, "session_expired", "Tu sesión expiró. Volvé a entrar.")
  }

  let body: { error?: string; message?: string } | null = null
  let parsed: unknown = null
  try {
    parsed = await res.json()
    body = parsed as { error?: string; message?: string }
  } catch {
    // Un 502 de proxy devuelve HTML, no JSON: se cae al statusText.
  }

  if (!res.ok) {
    // 403 NO cierra sesión: el usuario está autenticado, simplemente no es admin.
    // AuthGate lo traduce a la pantalla <NoAccess/>. Y el 503 de requireAdmin
    // (Supabase Auth caído) tampoco: llega como ApiError y se muestra como error,
    // conservando la sesión.
    throw new ApiError(
      res.status,
      body?.error ?? String(res.status),
      body?.message ?? res.statusText ?? "Error inesperado."
    )
  }

  return parsed as T
}

export const api = {
  me: (signal?: AbortSignal) => request<Me>("/me", { signal }),
  // `department` null = todos. No se manda el parámetro vacío: el servidor trata
  // ausente y "" igual, pero una URL sin basura es más fácil de leer en el Network tab.
  overtime: (month: MonthOnly, department: string | null, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ month })
    if (department) qs.set("department", department)
    return request<OvertimeResponse>(`/overtime?${qs}`, { signal })
  },
  // El detalle por solicitud (pestaña "Solicitudes"). Mismos convenios: null = sin
  // filtro y el parámetro no viaja.
  requests: (
    month: MonthOnly,
    department: string | null,
    status: RequestStatus | null,
    signal?: AbortSignal
  ) => {
    const qs = new URLSearchParams({ month })
    if (department) qs.set("department", department)
    if (status) qs.set("status", status)
    return request<OvertimeRequestsResponse>(`/requests?${qs}`, { signal })
  },
}
